/**
 * Change the sign-in email — a two-step dialog from the Email row of
 * Settings → Security (design approved 26 Sep 2026).
 *
 *   1. the new address  → a code goes to it (nothing changes yet)
 *   2. the code         → it becomes the sign-in email
 *
 * When a connected account signs in through the current address, step 1 says
 * that it keeps working. Renders `useChangeEmailDialog` and nothing else.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, CheckCircle2, Loader2 } from '@tonnie/assets';
import { isRefusedCode } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import { changeEmailSchema, type ChangeEmailInput } from '@tonnie/schemas';
import {
  Alert,
  AlertDescription,
  Button,
  CodeField,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@tonnie/ui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useChangeEmailDialog } from './hooks/use-change-email-dialog';

type EmailDialog = ReturnType<typeof useChangeEmailDialog>;

export function ChangeEmailDialog({ dialog }: { dialog: EmailDialog }) {
  const { t } = useTranslation();
  const { state } = dialog;
  const open = state.step !== 'idle';

  const onCodeStep =
    state.step === 'code_sent' ||
    state.step === 'verifying' ||
    (state.step === 'error' && state.phase !== 'send');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && dialog.close()}>
      <DialogContent
        className="sm:max-w-[480px]"
        closeLabel={t('common.close')}
        data-testid="settings-email-dialog"
        data-step={state.step}
      >
        {state.step === 'done' ? (
          <DoneStep dialog={dialog} email={state.email} linkedOld={state.linkedOld} />
        ) : state.step === 'old_kept' ? (
          <OldKeptStep dialog={dialog} email={state.email} oldEmail={state.oldEmail} />
        ) : onCodeStep ? (
          <CodeStep dialog={dialog} />
        ) : (
          <AddressStep dialog={dialog} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function StepLabel({ current }: { current: 1 | 2 }) {
  const { t } = useTranslation();
  return (
    <p className="text-xs font-semibold text-primary-accessible">
      {t('auth.security.emailDialog.step', { current, total: 2 })}
    </p>
  );
}

function AddressStep({ dialog }: { dialog: EmailDialog }) {
  const { t } = useTranslation();
  const { state, currentEmail } = dialog;
  const sending = state.step === 'sending';
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangeEmailInput>({ resolver: zodResolver(changeEmailSchema) });
  const [sameAsCurrent, setSameAsCurrent] = useState(false);

  const problem = errors.email
    ? t('validation.email.invalid')
    : sameAsCurrent
      ? t('profile.emailChange.sameAsCurrent')
      : state.step === 'email_taken'
        ? t('profile.emailChange.taken')
        : state.step === 'error'
          ? dialog.message
          : null;

  return (
    <form
      noValidate
      data-testid="settings-email-form"
      onSubmit={handleSubmit(({ email }) => {
        const unchanged = email === currentEmail.trim().toLowerCase();
        setSameAsCurrent(unchanged);
        if (!unchanged) void dialog.start(email);
      })}
    >
      <DialogHeader className="gap-1">
        <StepLabel current={1} />
        <DialogTitle className="text-lg font-semibold">
          {t('auth.security.emailDialog.title')}
        </DialogTitle>
        <DialogDescription className="text-sm">
          {t('auth.security.emailDialog.description')}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4 py-5">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t('auth.security.emailDialog.current')}</span>
          <span className="text-sm text-muted-foreground">{currentEmail}</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="settings-email-new" className="text-sm font-medium">
            {t('auth.security.emailDialog.new')}
          </Label>
          <Input
            id="settings-email-new"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder={t('auth.flow.emailPlaceholder')}
            className="h-11 text-base"
            aria-invalid={!!problem}
            aria-describedby={problem ? 'settings-email-new-error' : undefined}
            disabled={sending}
            data-testid="settings-email-new"
            {...register('email')}
          />
          {problem && (
            <p
              id="settings-email-new-error"
              role="alert"
              className="text-sm text-destructive-accessible"
            >
              {problem}
            </p>
          )}
        </div>
        {dialog.linkedProvider && (
          <p className="rounded-[10px] bg-(--tonnie-purple-02) px-3.5 py-3 text-sm leading-relaxed text-(--tonnie-purple-03)">
            {t('auth.security.emailDialog.linkedNote', { provider: dialog.linkedProvider })}
          </p>
        )}
      </div>
      <DialogFooter className="gap-2 border-t border-border pt-4">
        <Button type="button" variant="outline" onClick={dialog.close} disabled={sending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={sending} data-testid="settings-email-send">
          {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {t('auth.security.emailDialog.send')}
        </Button>
      </DialogFooter>
    </form>
  );
}

function CodeStep({ dialog }: { dialog: EmailDialog }) {
  const { t } = useTranslation();
  const { state } = dialog;
  const email = 'email' in state && state.email ? state.email : '';
  const verifying = state.step === 'verifying';
  const [code, setCode] = useState('');
  const problem = state.step === 'error' ? dialog.message : null;

  // A refused code is emptied, as on every code screen (`AuthCodeForm`): the
  // next one goes in whole instead of over the last box of the old one (cold
  // run 3, 29 Sep — 424242 after 000000 went out as 000004). A failed SWITCH
  // keeps it: that code was right, and Verify retries the switch with it.
  useEffect(() => {
    if (state.step === 'error' && state.phase === 'verify' && isRefusedCode(state.error)) {
      setCode('');
    }
  }, [state]);

  return (
    <form
      noValidate
      data-testid="settings-email-code-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.length === 6) void dialog.verify(email, code);
      }}
    >
      <DialogHeader className="gap-1">
        <StepLabel current={2} />
        <DialogTitle className="text-lg font-semibold">
          {t('auth.security.emailDialog.codeTitle')}
        </DialogTitle>
        <DialogDescription className="text-sm">
          {t('auth.security.emailDialog.codeDescription', { email })}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-3 py-5">
        <Label htmlFor="settings-email-code" className="text-sm font-medium">
          {t('auth.security.emailDialog.codeLabel')}
        </Label>
        <CodeField
          id="settings-email-code"
          value={code}
          onChange={setCode}
          disabled={verifying}
          invalid={!!problem}
          autoFocus
          aria-describedby={problem ? 'settings-email-code-error' : undefined}
          data-testid="settings-email-code"
        />
        {problem && (
          <p
            id="settings-email-code-error"
            role="alert"
            className="text-sm text-destructive-accessible"
          >
            {problem}
          </p>
        )}
        <button
          type="button"
          onClick={() => void dialog.resend(email)}
          disabled={verifying}
          className="self-start text-sm font-semibold text-primary-accessible hover:underline"
          data-testid="settings-email-resend"
        >
          {t('auth.security.emailDialog.resend')}
        </button>
      </div>
      {/* The same footer as step one and the password dialog: the secondary
          action outlined beside the primary, both on the right (ACC-13). */}
      <DialogFooter className="gap-2 border-t border-border pt-4">
        <Button type="button" variant="outline" onClick={dialog.open} disabled={verifying}>
          {t('auth.flow.back')}
        </Button>
        <Button
          type="submit"
          disabled={verifying || code.length !== 6}
          data-testid="settings-email-verify"
        >
          {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {t('auth.security.emailDialog.confirm')}
        </Button>
      </DialogFooter>
    </form>
  );
}

function DoneStep({
  dialog,
  email,
  linkedOld,
}: {
  dialog: EmailDialog;
  email: string;
  linkedOld: { email: string; provider: string | null } | null;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="settings-email-done">
      <DialogHeader className="gap-2">
        <CheckCircle2 className="h-9 w-9 text-success-accessible" aria-hidden="true" />
        <DialogTitle className="text-lg font-semibold">
          {t('auth.security.emailDialog.doneTitle')}
        </DialogTitle>
        <DialogDescription className="text-sm">
          {t('profile.emailChange.done', { email })}
        </DialogDescription>
      </DialogHeader>
      {linkedOld && (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="settings-email-linked-old">
          {linkedOld.provider
            ? t('profile.emailChange.oldLinked', {
                old: linkedOld.email,
                provider: linkedOld.provider,
              })
            : t('profile.emailChange.oldLinkedOther', { old: linkedOld.email })}
        </p>
      )}
      <DialogFooter className="mt-5 border-t border-border pt-4">
        <Button type="button" onClick={dialog.close}>
          {t('auth.security.emailDialog.close')}
        </Button>
      </DialogFooter>
    </div>
  );
}

function OldKeptStep({
  dialog,
  email,
  oldEmail,
}: {
  dialog: EmailDialog;
  email: string;
  oldEmail: string;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="settings-email-old-kept">
      <DialogHeader className="gap-2">
        <DialogTitle className="text-lg font-semibold">
          {t('auth.security.emailDialog.doneTitle')}
        </DialogTitle>
      </DialogHeader>
      <Alert variant="destructive" className="mt-4">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>
          {t('profile.emailChange.oldKept', { email, old: oldEmail })}
        </AlertDescription>
      </Alert>
      <DialogFooter className="mt-5 gap-2 border-t border-border pt-4">
        <Button type="button" variant="outline" onClick={dialog.close}>
          {t('auth.security.emailDialog.close')}
        </Button>
        <Button type="button" onClick={() => void dialog.retryRemoveOld(email, oldEmail)}>
          {t('profile.emailChange.removeOld')}
        </Button>
      </DialogFooter>
    </div>
  );
}
